import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  RotateCw,
  Pencil,
  Trash2,
  Search,
  Filter,
  Users,
} from 'lucide-react';
import { api } from '@/api/client';
import type {
  GetQuotasResp,
  GetResourcesResp,
  Quota,
  QuotaSelectorObject,
  ResourceType,
} from '@/api/types';
import { cn } from '@/lib/utils';
import { iconForResourceType } from '@/components/resources/TypePicker';

const SELECTOR_FILTERS: Array<QuotaSelectorObject | 'all'> = [
  'all',
  'User',
  'Project',
  'Department',
  'HPC Application',
];

export default function QuotasPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectorFilter, setSelectorFilter] = useState<
    QuotaSelectorObject | 'all'
  >('all');

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['quotas'],
    queryFn: () => api<GetQuotasResp>('/v1/quotas'),
    refetchOnWindowFocus: true,
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) =>
      api<unknown>(`/v1/quotas/${encodeURIComponent(name)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['quotas'] }),
    onError: (err) =>
      alert(`Delete failed: ${err instanceof Error ? err.message : String(err)}`),
  });

  // Resolve each quota's resource to its type so the Restriction Rule column
  // can show a per-type icon instead of one generic icon. Same endpoint
  // CreateQuotaPage already uses; a failure just falls back to the neutral
  // icon, so we don't gate the page on it.
  const { data: resourcesData } = useQuery({
    queryKey: ['resources'],
    queryFn: () => api<GetResourcesResp>('/v1/resources'),
  });
  const typeByResource = useMemo(() => {
    const m = new Map<string, ResourceType>();
    for (const r of resourcesData?.resources ?? []) m.set(r.name, r.type);
    return m;
  }, [resourcesData]);

  const all = data?.quotas ?? [];
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return all.filter((q) => {
      if (selectorFilter !== 'all' && q.selector_object !== selectorFilter)
        return false;
      if (needle === '') return true;
      return (
        q.name.toLowerCase().includes(needle) ||
        q.resource.toLowerCase().includes(needle) ||
        (q.description ?? '').toLowerCase().includes(needle) ||
        q.selector_regex.toLowerCase().includes(needle)
      );
    });
  }, [all, search, selectorFilter]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-semibold">Quotas</h1>
        <div className="flex gap-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border hover:bg-muted disabled:opacity-50 transition-colors"
          >
            <RotateCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            Refresh
          </button>
          <Link
            to="/quotas/new"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
          >
            <Plus className="h-4 w-4" />
            New Quota
          </Link>
        </div>
      </div>

      {isLoading && (
        <div className="py-12 text-center text-muted-foreground text-sm">
          Loading quotas…
        </div>
      )}

      {error && (
        <div className="py-3 px-4 rounded-md border border-destructive/30 bg-destructive/5 text-destructive text-sm">
          Failed to load quotas: {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      {!isLoading && !error && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative grow min-w-[200px]">
              <Search
                className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by name, resource, regex, or description"
                className="w-full pl-9 pr-3 py-2 rounded-md border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-colors"
              />
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Filter className="h-3.5 w-3.5" aria-hidden />
              <span>Selector:</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {SELECTOR_FILTERS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setSelectorFilter(opt)}
                  className={cn(
                    'px-2.5 py-1 rounded-md text-xs border transition-colors',
                    selectorFilter === opt
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border hover:bg-muted',
                  )}
                  aria-pressed={selectorFilter === opt}
                >
                  {opt === 'all' ? 'All' : opt}
                </button>
              ))}
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="px-3 py-2.5 font-medium">Name</th>
                  <th className="px-3 py-2.5 font-medium">Application selection</th>
                  <th className="px-3 py-2.5 font-medium">Restriction rule</th>
                  <th className="px-3 py-2.5 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {all.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="py-16 text-center text-muted-foreground"
                    >
                      No quotas yet. Click{' '}
                      <strong className="text-foreground">New Quota</strong> to
                      create one.
                    </td>
                  </tr>
                )}
                {all.length > 0 && filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="py-12 text-center text-muted-foreground text-sm"
                    >
                      No quotas match the current filters.
                    </td>
                  </tr>
                )}
                {filtered.map((q) => (
                  <QuotaRow
                    key={q.name}
                    quota={q}
                    resourceType={typeByResource.get(q.resource)}
                    onDelete={() => {
                      if (confirm(`Delete quota "${q.name}"?`)) {
                        deleteMutation.mutate(q.name);
                      }
                    }}
                    deleting={
                      deleteMutation.isPending &&
                      deleteMutation.variables === q.name
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

interface QuotaRowProps {
  quota: Quota;
  resourceType?: ResourceType;
  onDelete: () => void;
  deleting: boolean;
}

function QuotaRow({ quota, resourceType, onDelete, deleting }: QuotaRowProps) {
  const qName = encodeURIComponent(quota.name);
  const ResourceIcon = iconForResourceType(resourceType);
  return (
    <tr className="border-t hover:bg-muted/10 transition-colors">
      <td className="px-3 py-3 align-top">
        <div className="font-medium">{quota.name}</div>
        {quota.description && (
          <div className="text-xs text-muted-foreground mt-0.5 max-w-[28ch]">
            {quota.description}
          </div>
        )}
      </td>
      <td className="px-3 py-3 align-top">
        <div className="text-sm">{quota.application_selection}</div>
        <div className="mt-1 flex flex-wrap items-center gap-1">
          <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            {quota.selector_object}
          </span>
          <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            {quota.selection_operator === 1 ? 'For each' : 'For all'}
          </span>
        </div>
      </td>
      <td className="px-3 py-3 align-top">
        <div className="text-sm">{quota.restriction_rules}</div>
        <div
          className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground"
          title={
            resourceType
              ? `${resourceType} — resource this quota counts against`
              : 'Resource this quota counts against'
          }
          data-resource-type={resourceType ?? ''}
        >
          <ResourceIcon className="h-3 w-3" aria-hidden />
          <span>{quota.resource}</span>
        </div>
      </td>
      <td className="px-3 py-3 align-top text-right">
        <div className="inline-flex items-center gap-1">
          <MatchesAction quotaName={qName} count={quota.match_count ?? 0} />
          <Link
            to={`/quotas/${qName}/modify`}
            className="inline-flex items-center justify-center p-1.5 rounded hover:bg-muted transition-colors"
            title="Modify quota"
            aria-label="Modify quota"
          >
            <Pencil className="h-4 w-4" />
          </Link>
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            className="inline-flex items-center justify-center p-1.5 rounded text-destructive hover:bg-destructive/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title={deleting ? 'Deleting…' : 'Delete quota'}
            aria-label={deleting ? 'Deleting quota' : 'Delete quota'}
          >
            <Trash2 className={cn('h-4 w-4', deleting && 'animate-pulse')} />
          </button>
        </div>
      </td>
    </tr>
  );
}

// Icon + count badge that links to the per-quota matches page. The badge
// is intentionally neutral (muted bg, not destructive) — a match count
// signals activity, not urgency. Counts ≥ 100 collapse to "99+" so the
// pill stays narrow.
interface MatchesActionProps {
  quotaName: string;
  count: number;
}

function MatchesAction({ quotaName, count }: MatchesActionProps) {
  const display = count > 99 ? '99+' : String(count);
  const active = count > 0;
  return (
    <Link
      to={`/quotas/${quotaName}/matches`}
      className={cn(
        'relative inline-flex items-center justify-center p-1.5 rounded transition-colors',
        active
          ? 'text-foreground hover:bg-muted'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
      title={
        count === 0
          ? 'No allocations currently match this quota'
          : `${count} allocation${count === 1 ? '' : 's'} currently match this quota — click to view`
      }
      aria-label={`View matches (${count})`}
      data-match-count={count}
    >
      <Users className="h-4 w-4" />
      <span
        className={cn(
          'absolute -top-1 -right-1 min-w-[1.125rem] h-[1.125rem] px-1 inline-flex items-center justify-center rounded-full',
          'text-[10px] font-medium tabular-nums leading-none ring-1 ring-background',
          active
            ? 'bg-primary/15 text-foreground'
            : 'bg-muted text-muted-foreground',
        )}
      >
        {display}
      </span>
    </Link>
  );
}
